import type { EditorDraft } from '@/lib/layout-edit';

// §18: a save waits for every zone, so a floor is a draft for a long time.
export const draftKey = (venueId: string) => `kratispot.layout-draft.${venueId}`;

export type StoredDraft = { readonly at: string; readonly draft: EditorDraft };

// Compared as it is sent: two floors that differ nowhere are the same floor.
export function differs(one: EditorDraft, other: EditorDraft): boolean {
  return JSON.stringify(one) !== JSON.stringify(other);
}

export function keepDraft(venueId: string, draft: EditorDraft, at: string): void {
  try {
    window.localStorage.setItem(draftKey(venueId), JSON.stringify({ at, draft }));
  } catch {
    // A device that stores nothing keeps the draft in the tab, as before.
  }
}

export function forgetDraft(venueId: string): void {
  try {
    window.localStorage.removeItem(draftKey(venueId));
  } catch {
    // Nothing was stored, so nothing is left behind either.
  }
}

// A stored draft is offered, never applied: what the API holds is the floor.
export function storedDraft(venueId: string): StoredDraft | null {
  try {
    const found = window.localStorage.getItem(draftKey(venueId));
    return found === null ? null : readStored(JSON.parse(found));
  } catch {
    return null;
  }
}

function readStored(value: unknown): StoredDraft | null {
  const stored = value as Partial<StoredDraft> | null;
  if (typeof stored?.at !== 'string' || typeof stored.draft !== 'object') return null;
  const draft = stored.draft as Partial<EditorDraft> | null;
  if (!Array.isArray(draft?.spots) || !Array.isArray(draft.elements)) return null;
  return { at: stored.at, draft: stored.draft };
}
